/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {CompileReflector, ExternalReference, Identifiers, getUrlScheme, syntaxError} from '@angular/compiler';
import {ANALYZE_FOR_ENTRY_COMPONENTS, ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentFactory, ComponentFactoryResolver, ComponentRef, ElementRef, Injector, LOCALE_ID, NgModuleFactory, NgModuleRef, QueryList, Renderer, SecurityContext, TRANSLATIONS_FORMAT, TemplateRef, ViewContainerRef, ViewEncapsulation, ÉµCodegenComponentFactoryResolver, ÉµEMPTY_ARRAY, ÉµEMPTY_MAP, ÉµReflectionCapabilities as ReflectionCapabilities, Éµand, Éµccf, Éµcmf, Éµcrt, Éµdid, Éµeld, ÉµinlineInterpolate, Éµinterpolate, Éµmod, Éµmpd, Éµncd, Éµnov, Éµpad, Éµpid, Éµpod, Éµppd, Éµprd, Éµqud, ÉµregisterModuleFactory, Éµstringify as stringify, Éµted, Éµunv, Éµvid} from '@angular/core';

export const MODULE_SUFFIX = '';
const builtinExternalReferences = createBuiltinExternalReferencesMap();

export class JitReflector implements CompileReflector {
  private reflectionCapabilities: ReflectionCapabilities;
  private builtinExternalReferences = new Map<ExternalReference, any>();
  constructor() { this.reflectionCapabilities = new ReflectionCapabilities(); }
  componentModuleUrl(type: any, cmpMetadata: Component): string {
    const moduleId = cmpMetadata.moduleId;

    if (typeof moduleId === 'string') {
      const scheme = getUrlScheme(moduleId);
      return scheme ? moduleId : `package:${moduleId}${MODULE_SUFFIX}`;
    } else if (moduleId !== null && moduleId !== void 0) {
      throw syntaxError(
          `moduleId should be a string in "${stringify(type)}". See https://goo.gl/wIDDiL for more information.\n` +
          `If you're using Webpack you should inline the template and the styles, see https://goo.gl/X2J8zc.`);
    }

    return `./${stringify(type)}`;
  }
  parameters(typeOrFunc: /*Type*/ any): any[][] {
    return this.reflectionCapabilities.parameters(typeOrFunc);
  }
  tryAnnotations(typeOrFunc: /*Type*/ any): any[] { return this.annotations(typeOrFunc); }
  annotations(typeOrFunc: /*Type*/ any): any[] {
    return this.reflectionCapabilities.annotations(typeOrFunc);
  }
  shallowAnnotations(typeOrFunc: /*Type*/ any): any[] {
    throw new Error('Not supported in JIT mode');
  }
  propMetadata(typeOrFunc: /*Type*/ any): {[key: string]: any[]} {
    return this.reflectionCapabilities.propMetadata(typeOrFunc);
  }
  hasLifecycleHook(type: any, lcProperty: string): boolean {
    return this.reflectionCapabilities.hasLifecycleHook(type, lcProperty);
  }
  guards(type: any): {[key: string]: any} { return this.reflectionCapabilities.guards(type); }
  resolveExternalReference(ref: ExternalReference): any {
    return builtinExternalReferences.get(ref) || ref.runtime;
  }
}


function createBuiltinExternalReferencesMap() {
  const map = new Map<ExternalReference, any>();
  map.set(Identifiers.ANALYZE_FOR_ENTRY_COMPONENTS, ANALYZE_FOR_ENTRY_COMPONENTS);
  map.set(Identifiers.ElementRef, ElementRef);
  map.set(Identifiers.NgModuleRef, NgModuleRef);
  map.set(Identifiers.ViewContainerRef, ViewContainerRef);
  map.set(Identifiers.ChangeDetectorRef, ChangeDetectorRef);
  map.set(Identifiers.QueryList, QueryList);
  map.set(Identifiers.TemplateRef, TemplateRef);
  map.set(Identifiers.CodegenComponentFactoryResolver, ÉµCodegenComponentFactoryResolver);
  map.set(Identifiers.ComponentFactoryResolver, ComponentFactoryResolver);
  map.set(Identifiers.ComponentFactory, ComponentFactory);
  map.set(Identifiers.ComponentRef, ComponentRef);
  map.set(Identifiers.NgModuleFactory, NgModuleFactory);
  map.set(Identifiers.createModuleFactory, Éµcmf);
  map.set(Identifiers.moduleDef, Éµmod);
  map.set(Identifiers.moduleProviderDef, Éµmpd);
  map.set(Identifiers.RegisterModuleFactoryFn, ÉµregisterModuleFactory);
  map.set(Identifiers.Injector, Injector);
  map.set(Identifiers.ViewEncapsulation, ViewEncapsulation);
  map.set(Identifiers.ChangeDetectionStrategy, ChangeDetectionStrategy);
  map.set(Identifiers.SecurityContext, SecurityContext);
  map.set(Identifiers.LOCALE_ID, LOCALE_ID);
  map.set(Identifiers.TRANSLATIONS_FORMAT, TRANSLATIONS_FORMAT);
  map.set(Identifiers.inlineInterpolate, ÉµinlineInterpolate);
  map.set(Identifiers.interpolate, Éµinterpolate);
  map.set(Identifiers.EMPTY_ARRAY, ÉµEMPTY_ARRAY);
  map.set(Identifiers.EMPTY_MAP, ÉµEMPTY_MAP);
  map.set(Identifiers.Renderer, Renderer);
  map.set(Identifiers.viewDef, Éµvid);
  map.set(Identifiers.elementDef, Éµeld);
  map.set(Identifiers.anchorDef, Éµand);
  map.set(Identifiers.textDef, Éµted);
  map.set(Identifiers.directiveDef, Éµdid);
  map.set(Identifiers.providerDef, Éµprd);
  map.set(Identifiers.queryDef, Éµqud);
  map.set(Identifiers.pureArrayDef, Éµpad);
  map.set(Identifiers.pureObjectDef, Éµpod);
  map.set(Identifiers.purePipeDef, Éµppd);
  map.set(Identifiers.pipeDef, Éµpid);
  map.set(Identifiers.nodeValue, Éµnov);
  map.set(Identifiers.ngContentDef, Éµncd);
  map.set(Identifiers.unwrapValue, Éµunv);
  map.set(Identifiers.createRendererType2, Éµcrt);
  map.set(Identifiers.createComponentFactory, Éµccf);
  return map;
}